/*
 * DashboardGlossaryButton — a top-right "Glossary" button that opens a dialog
 * listing every KPI on the dashboard with its plain-English definition.
 * Definitions come from the shared analytics glossary (lib/analyticsGlossary).
 */
import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { BookOpen } from "lucide-react";
import { lookupMetric } from "@/lib/analyticsGlossary";

export default function DashboardGlossaryButton({ kpis = [] }) {
    const [open, setOpen] = useState(false);

    const items = useMemo(() => {
        const seen = new Set();
        const out = [];
        (kpis || []).forEach((k) => {
            const title = k?.title || k?.name || k?.label;
            if (!title) return;
            const norm = title.trim().toLowerCase();
            if (seen.has(norm)) return;
            seen.add(norm);
            out.push({ title, def: lookupMetric(title, k?.key || k?.metric_key || k?.id) });
        });
        return out;
    }, [kpis]);

    return (
        <>
            <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs"
                    onClick={() => setOpen(true)} data-testid="dashboard-glossary-btn">
                <BookOpen className="h-3.5 w-3.5 mr-1" /> Glossary
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="dashboard-glossary-dialog">
                    <DialogHeader>
                        <DialogTitle>Metric glossary</DialogTitle>
                        <DialogDescription>Plain-English definitions for the KPIs on this dashboard.</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-auto divide-y divide-border">
                        {items.length === 0 && (
                            <div className="py-6 text-center text-sm text-muted-foreground">No KPIs on this dashboard.</div>
                        )}
                        {items.map((it) => (
                            <div key={it.title} className="py-2.5" data-testid={`glossary-row-${it.title.toLowerCase().replace(/\s+/g, '-')}`}>
                                <div className="text-sm font-semibold">{it.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    {it.def || "No definition available yet."}
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
